import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    CardContent,
    Container,
    ThemeProvider,
    createTheme,
    CssBaseline,
    Typography,
    CircularProgress,
    Fade,
    useMediaQuery,
} from "@mui/material";
import { Error as ErrorIcon } from "@mui/icons-material";
import { verifyMobileLogin, fetchUserFaqs } from "../../api/api";
import Swal from "sweetalert2";
import easyKnowLogo from "../../assests/logo.png";
import { setCookie, getUserId, setUserAuthToken } from "../../utils/cookieUtils";

const theme = createTheme({
    palette: {
        primary: {
            main: "hsl(213, 100%, 50%)",
            light: "hsl(213, 100%, 65%)",
        },
        secondary: {
            main: "hsl(285, 85%, 58%)",
            light: "hsl(285, 85%, 70%)",
        },
        background: {
            default: "hsl(220, 25%, 97%)",
            paper: "#ffffff",
        },
        text: {
            primary: "hsl(220, 25%, 15%)",
            secondary: "hsl(220, 15%, 55%)",
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: "0 8px 30px -5px rgba(0, 0, 0, 0.08)",
                    border: "1px solid hsl(220, 15%, 92%)",
                    backdropFilter: "blur(10px)",
                },
            },
        },
    },
});

const VerifyMobile = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState("verifying"); // 'verifying', 'error' (success redirects immediately)
    const [message, setMessage] = useState("Verifying your login...");
    const hasVerifiedRef = useRef(false); // ✅ Prevent duplicate verification calls

    useEffect(() => {
        // ✅ Prevent running twice (React StrictMode in development)
        if (hasVerifiedRef.current) {
            return;
        }
        hasVerifiedRef.current = true;

        const verifyLogin = async () => {
           // console.log("Verifying Mobile Login...");
            const queryParams = new URLSearchParams(window.location.search);
            const user = queryParams.get("user");

            if (user) {
                try {
                    const response = await verifyMobileLogin(user);
                    const { data } = response;

                    if (data.status) {
                        console.log("Login successful!");
                        // Set cookies client-side with 90-day expiration (default)
                        setCookie("user_id", data.user.id); // Uses default 90 days
                        // Set company_id if provided by backend (validate it's not undefined/null)
                        if (data.user.company_id && data.user.company_id !== 'undefined' && data.user.company_id !== 'null') {
                            setCookie("company_id", data.user.company_id); // Uses default 90 days
                        }
                        // ✅ Store Sanctum authentication token
                        if (data.token) {
                            setUserAuthToken(data.token, 90);
                        }
                        
                        // ✅ Prefetch dashboard data in parallel while navigating
                        // This allows data to be ready when dashboard loads
                        const userId = data.user.id;
                        if (userId) {
                            fetchUserFaqs(userId).catch(err => console.log('Prefetch FAQs error:', err));
                        }
                        
                        // ✅ Navigate immediately - cookies are set synchronously
                        // Dispatch auth update event and navigate in the same frame
                        window.dispatchEvent(new Event('authUpdated'));
                        navigate("/dashboard", { replace: true });
                        
                        // Don't set status to "success" - just redirect immediately
                        return; // Exit early to prevent rendering success UI
                    } else {
                        console.log("Login failed:", data);
                        setStatus("error");
                        setMessage(data.message || "Login verification failed");
                        Swal.fire({
                            title: "Error!",
                            text: data.message,
                            icon: "error",
                            confirmButtonText: "OK",
                        });
                        setTimeout(() => {
                            navigate("/");
                        }, 2000);
                    }
                } catch (error) {
                    console.error("Error verifying mobile login:", error);
                    setStatus("error");
                    if (error.response) {
                        const data = error.response.data;
                        setMessage(data.message || "Something went wrong.");
                        Swal.fire({
                            title: "Error!",
                            text: data.message || "Something went wrong.",
                            icon: "error",
                            confirmButtonText: "OK",
                        });
                    } else {
                        setMessage(
                            error.message ||
                                "An error occurred during login verification."
                        );
                        // alert(
                        //     error.message ||
                        //         "An error occurred during login verification."
                        Swal.fire({
                            title: "Error!",
                            text: error.message || "An error occurred during login verification.",
                            icon: "error",
                            confirmButtonText: "OK",
                        })
                    }
                    setTimeout(() => {
                        navigate("/");
                    }, 2000);
                }
            } else {
                setStatus("error");
                setMessage("Invalid login response.");
               // alert("Invalid login response.");
               Swal.fire({
                title: "Error!",
                text: "Invalid login response.",
                icon: "error",
                confirmButtonText: "OK",
               });
                setTimeout(() => {
                    navigate("/");
                }, 2000);
            }
        };

        verifyLogin();
    }, [navigate]);

    const getStatusIcon = () => {
        // ✅ Only show icon for verifying or error states
        // Success state is skipped - user is redirected immediately
        if (status === "error") {
            return (
                <ErrorIcon
                    sx={{
                        fontSize: 64,
                        color: "error.main",
                        mb: 2,
                    }}
                />
            );
        }
        // Default: showing loading spinner while verifying
        return (
            <CircularProgress
                size={64}
                thickness={4}
                sx={{
                    mb: 2,
                    color: "primary.main",
                }}
            />
        );
    };

        const getStatusColor = () => {
        // ✅ Only return colors for verifying or error states
        if (status === "error") {
            return "error.main";
        }
        return "primary.main"; // Default for verifying state
    };

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box
                sx={{
                    minHeight: "100vh",
                    background:
                        "linear-gradient(135deg, hsl(220, 25%, 97%), hsl(220, 15%, 95%))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 2,
                    position: "relative",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                            "radial-gradient(ellipse 800px 600px at 50% 0%, hsl(213, 100%, 96%), transparent)",
                        pointerEvents: "none",
                    },
                }}
            >
                <Container maxWidth="sm">
                    <Fade in={true} timeout={500}>
                        <Card
                            sx={{
                                maxWidth: 450,
                                mx: "auto",
                                position: "relative",
                                overflow: "visible",
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: { xs: 4, sm: 5 },
                                    textAlign: "center",
                                }}
                            >
                                {/* Logo */}
                                <Box
                                    sx={{
                                        mb: 3,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                       component="img"
                                       src={easyKnowLogo}
                                      alt="easyKNOW Logo"
                                      sx={{
                                          width: 88,
                                          height: 88,
                                         // borderRadius: "12px",
                                         // background: "linear-gradient(135deg, hsl(213, 100%, 50%), hsl(285, 85%, 58%))",
                                          padding: "4px",
                                          objectFit: "contain",
                                      }}
                                    />
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            background: '#3f72af',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            fontSize: isMobile ? '2rem' : '3.0rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        easyKNOW
                                    </Typography>
                                </Box>

                                {/* Status Icon/Spinner */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        mb: 3,
                                    }}
                                >
                                    {getStatusIcon()}
                                </Box>

                                {/* Status Message */}
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: getStatusColor(),
                                        fontWeight: 600,
                                        mb: 1,
                                    }}
                                >
                                    {status === "verifying"
                                        ? "Verifying Login"
                                        : "Verification Failed"}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        mb: 2,
                                        fontWeight: 400,
                                    }}
                                >
                                    {message}
                                </Typography>

                                {/* Additional Info for Loading State */}
                                {status === "verifying" && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mt: 2,
                                            opacity: 0.7,
                                            fontStyle: "italic",
                                        }}
                                    >
                                        Please wait while we verify your
                                        credentials...
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Fade>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default VerifyMobile;
